import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, X, Maximize2, MessageSquare, Package, Lightbulb, Users, Linkedin, ChevronDown } from 'lucide-react';
import { SEO } from './components/SEO';
import { SelectedWorkPage } from './pages/SelectedWork';
// Placeholder data for the 9 screens
const CAROUSEL_IMAGES = [{
  id: 0,
  url: 'https://cdn.magicpatterns.com/uploads/hcQMYDev4TwRcAgS5kqpA5/1.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 1'
}, {
  id: 1,
  url: 'https://cdn.magicpatterns.com/uploads/hjU7NmVPpkijT9xRZ2Gc3f/2.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 2'
}, {
  id: 2,
  url: 'https://cdn.magicpatterns.com/uploads/uETTjm97DuwuH8YvWUxRWU/3.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 3'
}, {
  id: 3,
  url: 'https://cdn.magicpatterns.com/uploads/gPBDC2Q3bEUahn7o7Es2Hr/4.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 4'
}, {
  id: 4,
  url: 'https://cdn.magicpatterns.com/uploads/nEGJ4s1NfpgELDpP8Y5ZRE/5.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 5'
}, {
  id: 5,
  url: 'https://cdn.magicpatterns.com/uploads/kMS9pw6XCr6kjknhqGiiyM/6.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 6'
}, {
  id: 6,
  url: 'https://cdn.magicpatterns.com/uploads/oHxwsXa1S9h1ATV3Hjgwsi/7.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 7'
}, {
  id: 7,
  url: 'https://cdn.magicpatterns.com/uploads/vtqNM2yXaeD6SagUwMRnSy/8.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 8'
}, {
  id: 8,
  url: 'https://cdn.magicpatterns.com/uploads/x8Css7KrG1bx7WifQJQZBX/9.png',
  alt: 'Secure Messenger social media campaign - creative content strategy screen 9'
}];
const TELECOM_IMAGES = [{
  id: 0,
  url: 'https://cdn.magicpatterns.com/uploads/o3MrpVKrWQA537A6JPWWVB/Why_V-Tell_1.png',
  alt: 'Global telecom brand positioning strategy - market positioning framework slide 1'
}, {
  id: 1,
  url: 'https://cdn.magicpatterns.com/uploads/kHvJAiXgTFgpaF6ZsB3xZo/Why_V-Tell_2.png',
  alt: 'Global telecom brand positioning strategy - market positioning framework slide 2'
}, {
  id: 2,
  url: 'https://cdn.magicpatterns.com/uploads/sqvr4L1xw67R54aVHoJryA/Why_V-Tell_3.png',
  alt: 'Global telecom brand positioning strategy - market positioning framework slide 3'
}, {
  id: 3,
  url: 'https://cdn.magicpatterns.com/uploads/61Kf1HtYNdtBsRjRagFtPK/THE_WORLD_IS_WITHIN_YOUR_TOUCH.png',
  alt: 'Global telecom brand positioning strategy - The World is Within Your Touch campaign'
}];
function TelecomCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const nextSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % TELECOM_IMAGES.length);
  };
  const prevSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + TELECOM_IMAGES.length) % TELECOM_IMAGES.length);
  };
  const goToSlide = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);
  return <>
      <div data-id="element-0">
        {/* Carousel Container */}
        <div className="aspect-[4/3] bg-white mb-6 relative overflow-hidden flex items-center justify-center p-6 group/carousel cursor-pointer" onClick={() => setIsModalOpen(true)} data-id="element-1">
          {/* Main Image */}
          <div className="w-full h-full relative flex items-center justify-center bg-gray-50" data-id="element-2">
            <img src={TELECOM_IMAGES[currentIndex].url} alt={TELECOM_IMAGES[currentIndex].alt} className="max-w-full max-h-full object-contain transition-opacity duration-300" data-id="element-3" />

            {/* Hover Overlay with "View" hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 bg-black/10 pointer-events-none" data-id="element-4">
              <span className="bg-white/90 text-[#E8693B] px-3 py-1.5 text-xs font-medium rounded-full shadow-sm flex items-center gap-1.5" data-id="element-5">
                <Maximize2 className="w-3 h-3" data-id="element-6" />
                View Fullscreen
              </span>
            </div>
          </div>

          {/* Navigation Arrows (Visible on hover) */}
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Previous slide" data-id="element-7">
            <ChevronLeft className="w-5 h-5" data-id="element-8" />
          </button>
          <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Next slide" data-id="element-9">
            <ChevronRight className="w-5 h-5" data-id="element-10" />
          </button>

          {/* Bullet Indicators */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10" data-id="element-11">
            {TELECOM_IMAGES.map((_, idx) => <button key={idx} onClick={e => goToSlide(idx, e)} className={`w-2 h-2 rounded-full transition-all shadow-sm ${idx === currentIndex ? 'bg-[#E8693B] w-4' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-12" />)}
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isModalOpen && <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setIsModalOpen(false)} data-id="element-13">
          <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors p-2" data-id="element-14">
            <X className="w-8 h-8" data-id="element-15" />
          </button>

          <div className="relative w-full max-w-6xl max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-16">
            <div className="relative w-full h-full flex items-center justify-center" data-id="element-17">
              <img src={TELECOM_IMAGES[currentIndex].url} alt={TELECOM_IMAGES[currentIndex].alt} className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-sm bg-white" data-id="element-18" />

              {/* Modal Navigation */}
              <button onClick={prevSlide} className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-19">
                <ChevronLeft className="w-8 h-8" data-id="element-20" />
              </button>
              <button onClick={nextSlide} className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-21">
                <ChevronRight className="w-8 h-8" data-id="element-22" />
              </button>
            </div>

            <div className="mt-4 text-white/60 text-sm font-medium tracking-widest" data-id="element-23">
              {currentIndex + 1} / {TELECOM_IMAGES.length}
            </div>
          </div>
        </div>}
    </>;
}
function ProjectCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const nextSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
  };
  const prevSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  };
  const goToSlide = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };
  // Handle keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);
  return <>
      <article className="group cursor-pointer" data-id="element-24">
        {/* Carousel Container */}
        <div className="aspect-[4/3] bg-white mb-6 relative overflow-hidden flex items-center justify-center p-6 group/carousel" onClick={() => setIsModalOpen(true)} data-id="element-25">
          {/* Main Image */}
          <div className="w-full h-full relative" data-id="element-26">
            <img src={CAROUSEL_IMAGES[currentIndex].url} alt={CAROUSEL_IMAGES[currentIndex].alt} className="w-full h-full object-contain transition-opacity duration-300" data-id="element-27" />

            {/* Hover Overlay with "View" hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 bg-black/10 pointer-events-none" data-id="element-28">
              <span className="bg-white/90 text-[#E8693B] px-3 py-1.5 text-xs font-medium rounded-full shadow-sm flex items-center gap-1.5" data-id="element-29">
                <Maximize2 className="w-3 h-3" data-id="element-30" />
                View Fullscreen
              </span>
            </div>
          </div>

          {/* Navigation Arrows (Visible on hover) */}
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Previous slide" data-id="element-31">
            <ChevronLeft className="w-5 h-5" data-id="element-32" />
          </button>
          <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Next slide" data-id="element-33">
            <ChevronRight className="w-5 h-5" data-id="element-34" />
          </button>

          {/* Bullet Indicators */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10" data-id="element-35">
            {CAROUSEL_IMAGES.map((_, idx) => <button key={idx} onClick={e => goToSlide(idx, e)} className={`w-2 h-2 rounded-full transition-all shadow-sm ${idx === currentIndex ? 'bg-[#E8693B] w-4' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-36" />)}
          </div>
        </div>

        <h3 className="text-2xl font-bold mb-2 group-hover:underline decoration-2 underline-offset-4 min-h-[2rem]" data-id="element-37">
          Secure Messenger
        </h3>
        <p className="text-white/80 text-sm md:text-base leading-relaxed" data-id="element-38">
          Social media strategy that increased social media visits by 10x
        </p>
      </article>

      {/* Fullscreen Modal */}
      {isModalOpen && <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setIsModalOpen(false)} data-id="element-39">
          <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors p-2" data-id="element-40">
            <X className="w-8 h-8" data-id="element-41" />
          </button>

          <div className="relative w-full max-w-6xl max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-42">
            <div className="relative w-full h-full flex items-center justify-center" data-id="element-43">
              <img src={CAROUSEL_IMAGES[currentIndex].url} alt={CAROUSEL_IMAGES[currentIndex].alt} className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-sm bg-white" data-id="element-44" />

              {/* Modal Navigation */}
              <button onClick={prevSlide} className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-45">
                <ChevronLeft className="w-8 h-8" data-id="element-46" />
              </button>
              <button onClick={nextSlide} className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-47">
                <ChevronRight className="w-8 h-8" data-id="element-48" />
              </button>
            </div>

            <div className="mt-4 text-white/60 text-sm font-medium tracking-widest" data-id="element-49">
              {currentIndex + 1} / {CAROUSEL_IMAGES.length}
            </div>
          </div>
        </div>}
    </>;
}
function SecureMessengerCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const nextSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
  };
  const prevSlide = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  };
  const goToSlide = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };
  // Handle keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);
  return <>
      <div data-id="element-50">
        {/* Carousel Container */}
        <div className="aspect-[4/3] bg-white mb-6 relative overflow-hidden flex items-center justify-center p-6 group/carousel cursor-pointer" onClick={() => setIsModalOpen(true)} data-id="element-51">
          {/* Main Image */}
          <div className="w-full h-full relative" data-id="element-52">
            <img src={CAROUSEL_IMAGES[currentIndex].url} alt={CAROUSEL_IMAGES[currentIndex].alt} className="w-full h-full object-contain transition-opacity duration-300" data-id="element-53" />

            {/* Hover Overlay with "View" hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 bg-black/10 pointer-events-none" data-id="element-54">
              <span className="bg-white/90 text-[#E8693B] px-3 py-1.5 text-xs font-medium rounded-full shadow-sm flex items-center gap-1.5" data-id="element-55">
                <Maximize2 className="w-3 h-3" data-id="element-56" />
                View Fullscreen
              </span>
            </div>
          </div>

          {/* Navigation Arrows (Visible on hover) */}
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Previous slide" data-id="element-57">
            <ChevronLeft className="w-5 h-5" data-id="element-58" />
          </button>
          <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-[#E8693B] rounded-full shadow-md opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-[#E8693B] hover:text-white z-10" aria-label="Next slide" data-id="element-59">
            <ChevronRight className="w-5 h-5" data-id="element-60" />
          </button>

          {/* Bullet Indicators */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10" data-id="element-61">
            {CAROUSEL_IMAGES.map((_, idx) => <button key={idx} onClick={e => goToSlide(idx, e)} className={`w-2 h-2 rounded-full transition-all shadow-sm ${idx === currentIndex ? 'bg-[#E8693B] w-4' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-62" />)}
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isModalOpen && <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setIsModalOpen(false)} data-id="element-63">
          <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors p-2" data-id="element-64">
            <X className="w-8 h-8" data-id="element-65" />
          </button>

          <div className="relative w-full max-w-6xl max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-66">
            <div className="relative w-full h-full flex items-center justify-center" data-id="element-67">
              <img src={CAROUSEL_IMAGES[currentIndex].url} alt={CAROUSEL_IMAGES[currentIndex].alt} className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-sm bg-white" data-id="element-68" />

              {/* Modal Navigation */}
              <button onClick={prevSlide} className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-69">
                <ChevronLeft className="w-8 h-8" data-id="element-70" />
              </button>
              <button onClick={nextSlide} className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all" data-id="element-71">
                <ChevronRight className="w-8 h-8" data-id="element-72" />
              </button>
            </div>

            <div className="mt-4 text-white/60 text-sm font-medium tracking-widest" data-id="element-73">
              {currentIndex + 1} / {CAROUSEL_IMAGES.length}
            </div>
          </div>
        </div>}
    </>;
}
function MainPortfolio() {
  useEffect(() => {
    // Scroll to section if hash is present in URL
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    }
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      // Intersection Observer for scroll animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      }, observerOptions);
      // Observe all elements with animate-on-scroll class
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach(el => {
        observer.observe(el);
        // Check if element is already in viewport on page load
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) {
          // Add class after a tiny delay to ensure animation triggers
          setTimeout(() => {
            el.classList.add('is-visible');
          }, 50);
        }
      });
      return () => observer.disconnect();
    }, 100);
  }, []);
  return <>
      <SEO data-id="element-74" />
      <div className="min-h-screen w-full bg-[#E8693B] text-white font-sans selection:bg-white selection:text-[#E8693B]" data-id="element-75">
        {/* Header Navigation */}
        <header className="w-full bg-white/5 backdrop-blur-sm sticky top-0 z-50 border-b border-white/10" data-id="element-76">
          <div className="w-full max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between" data-id="element-77">
            <div className="flex items-center gap-3" data-id="element-78">
              <span className="text-lg md:text-xl font-semibold" data-id="element-79">
                Katia Engalycheva
              </span>
              <span className="text-white/50" data-id="element-80">—</span>
              <span className="text-sm md:text-base text-white font-light" data-id="element-81">
                Marketing & Product Strategist
              </span>
            </div>

            <nav className="flex items-center gap-8" data-id="element-82">
              <a href="#about" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-83">
                About
              </a>
              <a href="#work" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-84">
                Selected Work
              </a>
              <a href="#speaking" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-85">
                Speaking
              </a>
              <a href="#contact" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-86">
                Contact
              </a>
            </nav>
          </div>
        </header>

        <main className="w-full max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-20" data-id="element-87">
          {/* Section 1: Introduction */}
          <section className="min-h-[60vh] flex flex-col justify-center mb-32 -mt-16" data-id="element-88">
            <div className="max-w-3xl space-y-8" data-id="element-89">
              <h1 className="text-3xl md:text-5xl font-bold leading-tight tracking-tight" data-id="element-90">
                Marketing & Product Strategist working at the intersection of
                growth, marketing, and monetization.
              </h1>

              <div className="space-y-6 text-lg md:text-xl leading-relaxed opacity-90 font-light max-w-2xl" data-id="element-91">
                <p data-id="element-92">
                  I translate deep user empathy and complex market needs into
                  scalable products and the compelling narratives that drive
                  them to market.
                </p>
              </div>
            </div>

            <a href="#what-i-do" className="text-center mt-32 space-y-4 block cursor-pointer group" data-id="element-93">
              <p className="text-white/60 text-base font-light group-hover:text-white/80 transition-colors" data-id="element-94">
                Go ahead, take a peep at some work I've done.
              </p>
              <div className="flex justify-center" data-id="element-95">
                <ChevronDown className="w-8 h-8 text-white/60 animate-bounce group-hover:text-white/80 transition-colors" data-id="element-96" />
              </div>
            </a>
          </section>{' '}
          {/* About Section - Editorial Magazine Layout */}
          <section id="about" className="mb-32 -mx-6 md:-mx-12 px-6 md:px-12 py-20 bg-gray-100" data-id="element-97">
            <div className="max-w-4xl mx-auto" data-id="element-98">
              {/* Eyebrow heading with top rule - centered */}
              <div className="border-t border-gray-400 pt-8 mb-12 animate-on-scroll text-center" data-id="element-99">
                <h2 className="text-xs uppercase tracking-[0.2em] text-[#E8693B] font-medium" data-id="element-100">
                  About
                </h2>
              </div>

              {/* Editorial text with single column layout */}
              <div className="animate-on-scroll animation-delay-200" data-id="element-101">
                <div className="text-gray-900" data-id="element-102">
                  <p className="text-lg leading-relaxed text-justify" data-id="element-103">
                    My work is built on a simple idea:{' '}
                    <span className="underline decoration-[#E8693B] decoration-2 underline-offset-2" data-id="element-104">
                      a great product is inseparable from the story
                    </span>{' '}
                    that brings it to market.
                  </p>

                  <p className="text-lg leading-relaxed mt-6 text-justify" data-id="element-105">
                    I'm a strategist who bridges product vision and market
                    reality. My work starts by dissecting complex challenges -
                    whether launching apps in crowded markets or entering new
                    territories for brands like Williams F1. What ties it
                    together is a focus on{' '}
                    <span className="underline decoration-[#E8693B] decoration-2 underline-offset-2" data-id="element-106">
                      actionable insights
                    </span>
                    : translating research into clear frameworks that drive
                    growth, build trust, and create measurable impact.
                  </p>
                </div>
              </div>

              {/* Bottom rule */}
              <div className="border-b border-gray-400 mt-12" data-id="element-107"></div>
            </div>
          </section>
          {/* Section 1.5: What I do */}
          <section id="what-i-do" className="mb-32 -mx-6 md:-mx-12 px-6 md:px-12 py-16 bg-[#ED8159]" data-id="element-108">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16" data-id="element-109">
              What I do
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16" data-id="element-110">
              {/* Communications Lead */}
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-111">
                <div className="flex items-center gap-4 mb-4" data-id="element-112">
                  <MessageSquare className="w-10 h-10 text-[#E8693B] flex-shrink-0" strokeWidth={1.5} data-id="element-113" />
                  <h3 className="text-2xl font-bold text-[#E8693B]" data-id="element-114">
                    Communications Lead
                  </h3>
                </div>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed font-light" data-id="element-115">
                  Translate complex into simple stories. Create tailored
                  narratives that resonate with any audience — internal or
                  external.
                </p>
              </div>

              {/* Product Manager */}
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-116">
                <div className="flex items-center gap-4 mb-4" data-id="element-117">
                  <Package className="w-10 h-10 text-[#E8693B] flex-shrink-0" strokeWidth={1.5} data-id="element-118" />
                  <h3 className="text-2xl font-bold text-[#E8693B]" data-id="element-119">
                    Product Manager
                  </h3>
                </div>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed font-light" data-id="element-120">
                  Define a product's vision and goals. Manage the entire product
                  lifecycle from ideation to launch and iteration.
                </p>
              </div>

              {/* Creative Strategy */}
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-121">
                <div className="flex items-center gap-4 mb-4" data-id="element-122">
                  <Lightbulb className="w-10 h-10 text-[#E8693B] flex-shrink-0" strokeWidth={1.5} data-id="element-123" />
                  <h3 className="text-2xl font-bold text-[#E8693B]" data-id="element-124">
                    Creative Strategy
                  </h3>
                </div>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed font-light" data-id="element-125">
                  Transform strategic vision into tangible results by
                  identifying unconventional solutions.
                </p>
              </div>

              {/* Team Leadership */}
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-126">
                <div className="flex items-center gap-4 mb-4" data-id="element-127">
                  <Users className="w-10 h-10 text-[#E8693B] flex-shrink-0" strokeWidth={1.5} data-id="element-128" />
                  <h3 className="text-2xl font-bold text-[#E8693B]" data-id="element-129">
                    Team Leadership
                  </h3>
                </div>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed font-light" data-id="element-130">
                  Foster cross-functional colloboration.
                </p>
              </div>
            </div>
          </section>
          {/* Section 2: Selected Work */}
          <section id="work" className="mb-32" data-id="element-131">
            <div className="flex items-center gap-4 mb-12 border-b border-white/30 pb-4" data-id="element-132">
              <span className="text-sm font-medium uppercase tracking-widest opacity-80" data-id="element-133">
                01
              </span>
              <h2 className="text-xl font-semibold" data-id="element-134">Selected Work</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16" data-id="element-135">
              {/* Project 0 - Sochi Olympics VISA Campaign */}
              <article className="group" data-id="element-136">
                <h3 className="text-2xl font-bold mb-4" data-id="element-137">
                  Product-Led Growth: Sochi Olympics VISA Subway Campaign
                </h3>
                <div className="aspect-[4/3] bg-white mb-6 transition-colors group-hover:bg-white/95 relative overflow-hidden flex items-center justify-center" data-id="element-138">
                  <img src="https://cdn.magicpatterns.com/uploads/iWLCSVNhWnBFJoy6E96LDH/BBC.png" alt="Sochi Olympics VISA Subway Campaign - product-led growth marketing activation featuring gamified fitness experience" className="w-full h-full object-cover" data-id="element-139" />
                </div>
                <div className="flex justify-center" data-id="element-140">
                  <a href="/selected-work#sochi-olympics-visa" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-141">
                    More
                  </a>
                </div>
              </article>

              {/* Project 1 - Williams F1 */}
              <article className="group" data-id="element-142">
                <h3 className="text-2xl font-bold mb-4" data-id="element-143">
                  Go-To-Market Strategy: Williams F1 Market Expansion
                </h3>
                <div className="aspect-[4/3] bg-white mb-6 transition-colors group-hover:bg-white/95 relative overflow-hidden flex items-center justify-center p-6" data-id="element-144">
                  <img src="https://cdn.magicpatterns.com/uploads/oYJi223opLLLFjinotteKt/williams-f1-logo-png_seeklogo-241392.png" alt="Williams F1 Racing logo - go-to-market strategy for international market expansion" className="w-full h-full object-contain" data-id="element-145" />
                </div>
                <div className="flex justify-center" data-id="element-146">
                  <a href="/selected-work#williams-f1" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-147">
                    More
                  </a>
                </div>
              </article>

              {/* Project 2 - Mobile VPN App */}
              <article className="group" data-id="element-148">
                <h3 className="text-2xl font-bold mb-4" data-id="element-149">
                  Product Strategy & UX Design: Mobile App
                </h3>
                <div className="aspect-[4/3] bg-white mb-6 transition-colors group-hover:bg-white/95 relative overflow-hidden flex items-center justify-center p-6" data-id="element-150">
                  <img src="https://cdn.magicpatterns.com/uploads/t74dTaEva5dnPp1AmnPky5/Untitled.jpg" alt="Mobile app product strategy and UX design - privacy and security application development" className="w-full h-full object-contain" data-id="element-151" />
                </div>
                <div className="flex justify-center" data-id="element-152">
                  <a href="/selected-work#mobile-app" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-153">
                    More
                  </a>
                </div>
              </article>

              {/* Project 3 - Secure Messenger */}
              <article className="group" data-id="element-154">
                <h3 className="text-2xl font-bold mb-4" data-id="element-155">
                  Secure Messenger: Product Growth and Social Media Strategy
                </h3>
                <SecureMessengerCarousel data-id="element-156" />
                <div className="flex justify-center mt-6" data-id="element-157">
                  <a href="/selected-work#secure-messenger" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-158">
                    More
                  </a>
                </div>
              </article>

              {/* Project 4 */}
              <article className="group" data-id="element-159">
                <h3 className="text-2xl font-bold mb-4" data-id="element-160">
                  Global Telecom Brand: Developed a New Market Positioning
                  Strategy
                </h3>
                <TelecomCarousel data-id="element-161" />
                <div className="flex justify-center mt-6" data-id="element-162">
                  <a href="/selected-work#telecom-brand" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-163">
                    More
                  </a>
                </div>
              </article>

              {/* Project 5 */}
              <article className="group" data-id="element-164">
                <h3 className="text-2xl font-bold mb-4" data-id="element-165">
                  Stakeholder Analysis for the International Olympic Committee
                </h3>
                <div className="aspect-[4/3] bg-white mb-6 transition-colors group-hover:bg-white/95 relative overflow-hidden flex items-center justify-center p-6" data-id="element-166">
                  <img src="https://cdn.magicpatterns.com/uploads/7LRpnfKFZbzrVKmgd4MkjF/IOC.png" alt="International Olympic Committee logo - stakeholder ecosystem analysis and strategic planning research" className="w-full h-full object-contain" data-id="element-167" />
                </div>
                <div className="flex justify-center" data-id="element-168">
                  <a href="/selected-work#ioc" className="inline-block px-8 py-3 bg-white text-[#E8693B] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg" data-id="element-169">
                    More
                  </a>
                </div>
              </article>
            </div>
          </section>
          {/* Section 2.5: Speaking */}
          <section id="speaking" className="mb-32" data-id="element-170">
            <div className="flex items-center gap-4 mb-12 border-b border-white/30 pb-4" data-id="element-171">
              <span className="text-sm font-medium uppercase tracking-widest opacity-80" data-id="element-172">
                02
              </span>
              <h2 className="text-xl font-semibold" data-id="element-173">Speaking</h2>
            </div>

            <div className="space-y-16" data-id="element-174">
              {/* Uzbekistan 2025 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-175">
                <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-start justify-center rounded-sm" data-id="element-176">
                  <img src="https://cdn.magicpatterns.com/uploads/vh42dJr7JdTLHL6zJLq1Ab/Uzbekistan_speech_photo.jpg" alt="Katia Engalycheva speaking at international congress in Uzbekistan about AI-based psychological technologies" className="w-full h-full object-cover" data-id="element-177" />
                </div>
                <div data-id="element-178">
                  <h3 className="text-2xl font-bold leading-none -mt-1.5 mb-6" data-id="element-179">
                    Congress in Uzbekistan, 2025
                  </h3>{' '}
                  <p className="text-white/80 text-lg leading-relaxed font-light text-justify" data-id="element-180">
                    I was invited as one of the key speakers at an international
                    congress to discuss AI-based psychological technologies.
                  </p>
                </div>
              </div>

              {/* ICPC 2021 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-181">
                <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-start justify-center rounded-sm" data-id="element-182">
                  <img src="https://cdn.magicpatterns.com/uploads/pGoAYsjrnyyucVasYSPFyt/ICPC_photo_75_percent_resized.jpg" alt="Katia Engalycheva speaking at ICPC World Finals Russia 2021 - International Collegiate Programming Contest" className="w-full h-full object-cover" data-id="element-183" />
                </div>
                <div data-id="element-184">
                  <h3 className="text-2xl font-bold leading-none -mt-1.5 mb-6" data-id="element-185">
                    ICPC (International Collegiate Programming Contest) World
                    Finals in Russia, 2021
                  </h3>{' '}
                  <p className="text-white/80 text-lg leading-relaxed font-light text-justify" data-id="element-186">
                    As a member of the ICPC 2021 World Finals Organizing
                    Committee, I delivered a talk to event volunteers on
                    communication, teamwork, and global engagement.
                  </p>
                </div>
              </div>

              {/* Peru 2018 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-187">
                <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-start justify-center rounded-sm" data-id="element-188">
                  <img src="https://cdn.magicpatterns.com/uploads/nZ3Uf1HEBUW4CuRiGYjrDz/уругваи_спич.jpg" alt="Katia Engalycheva speaking at Peruvian University of Applied Sciences about communication, storytelling, and cultural awareness" className="w-full h-full object-cover" data-id="element-189" />
                </div>
                <div data-id="element-190">
                  <h3 className="text-2xl font-bold leading-none -mt-1.5 mb-6" data-id="element-191">
                    Peru, 2018
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed font-light text-justify" data-id="element-192">
                    Invited to speak on the importance of communication,
                    storytelling skills, and cultural awareness at a leading
                    university in Peru (Peruvian University of Applied Sciences)
                    as part of an international outreach program.
                  </p>
                </div>
              </div>

              {/* Argentina 2018 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-193">
                <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-start justify-center rounded-sm" data-id="element-194">
                  <img src="https://cdn.magicpatterns.com/uploads/1mqiEvsy5zYznS6y3bCTcy/make_my_white_shirt_look_clean_and_tidy_but_dont_change_anything_else_on_this_photo.jpg" alt="Katia Engalycheva interview at radio station in Argentina discussing international outreach work" className="w-full h-full object-cover" data-id="element-195" />
                </div>
                <div data-id="element-196">
                  <h3 className="text-2xl font-bold leading-none -mt-1.5 mb-6" data-id="element-197">
                    Argentina, 2018
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed font-light text-justify" data-id="element-198">
                    An interview at a radio station discussing marketing for
                    social causes as part of an international outreach program.
                  </p>
                </div>
              </div>

              {/* Slovenia 2018 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-199">
                <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-start justify-center rounded-sm" data-id="element-200">
                  <img src="https://cdn.magicpatterns.com/uploads/uxqX7X2mqd2fuy4ZgitDM8/epeka_new_clothing_photo.jpg" alt="Katia Engalycheva featured in press conference on social entrepreneurship in Europe, Slovenia 2018" className="w-full h-full object-cover" data-id="element-201" />
                </div>
                <div data-id="element-202">
                  <h3 className="text-2xl font-bold leading-none -mt-1.5 mb-6" data-id="element-203">
                    Slovenia, 2018
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed font-light text-justify" data-id="element-204">
                    Featured in a press conference on social entrepreneurship in
                    Europe.
                  </p>
                </div>
              </div>
            </div>
          </section>
          {/* Section 3: Contact */}
          <section id="contact" className="mb-20" data-id="element-205">
            <div className="text-center" data-id="element-206">
              <a href="mailto:attheideasboutique@gmail.com" className="inline-block px-12 py-6 bg-white text-[#E8693B] text-3xl md:text-4xl font-bold rounded-2xl hover:bg-white/90 transition-all shadow-lg hover:shadow-xl" data-id="element-207">
                Get in Touch
              </a>

              <div className="mt-8" data-id="element-208">
                <a href="https://www.linkedin.com/in/katiaengalycheva/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-12 h-12 bg-white text-[#E8693B] rounded-full hover:bg-white/90 transition-all shadow-md hover:shadow-lg" aria-label="LinkedIn Profile" data-id="element-209">
                  <Linkedin className="w-6 h-6" strokeWidth={1.5} data-id="element-210" />
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>;
}
export function App() {
  return <Routes data-id="element-211">
      <Route path="/" element={<MainPortfolio data-id="element-213" />} data-id="element-212" />
      <Route path="/selected-work" element={<SelectedWorkPage data-id="element-215" />} data-id="element-214" />
    </Routes>;
}